import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DeploymentAdminModel,
  IndexAdminModel,
  IndexRole,
  ModelService,
  ReasoningEffort
} from 'digiteq-ai-portal-client-lib';
import { MessageService } from 'primeng/api';
import { FormUtils } from '../../utils/form-utils';
import {ErrorService} from "../../services/error.service";
import {finalize, Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-new-daise-index-dialog',
  templateUrl: './new-daise-index-dialog.component.html',
  styleUrl: './new-daise-index-dialog.component.scss'
})
export class NewDaiseIndexDialogComponent implements OnInit, OnDestroy {
  private _editedItem: IndexAdminModel | null = null;

  public get editedItem(): IndexAdminModel | null {
    return this._editedItem;
  }

  @Input() public set editedItem(value: IndexAdminModel | null) {
    this._editedItem = value;
    this.initForm();
  }

  @Input() processing: boolean = false;

  @Output() indexCancelled = new EventEmitter<void>();
  @Output() indexAdded = new EventEmitter<IndexAdminModel>();
  @Output() indexEdited = new EventEmitter<IndexAdminModel>();
  @Output() loadingErrorOccured = new EventEmitter<void>();

  newIndexForm?: FormGroup;
  models: DeploymentAdminModel[] = [];
  loading: boolean = false;

  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  protected readonly roleOptions: string[] = Object.values(IndexRole);
  protected readonly reasoningEffortLevelOptions: { label: string; value: ReasoningEffort }[] =
    Object.entries(ReasoningEffort).map(([label, value]) => ({
      label,
      value,
    }));
  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private readonly modelService: ModelService,
    private readonly errorService: ErrorService,

  ) {}

  ngOnInit(): void {
    this. loadModels();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  loadModels() {
    this.loading = true;
    this.modelService.getModelsAdmin().pipe(
      takeUntil(this.ngUnsubscribe$),
      finalize(() => { this.loading = false})
    )
      .subscribe({
      next: data => {
        this.models = data.filter(item => item.enabled)
      },
      error: err => {
        this.errorService.handleError(err, 'Could not load models');
        this.loadingErrorOccured.emit();
      }
    })
  }

  initForm() {
    this.newIndexForm = this.fb.group({
      name: [this.editedItem?.name || '', Validators.required],
      indexName: [this.editedItem?.indexName || '', Validators.required],
      endpoint: [this.editedItem?.endpoint || '', Validators.required],
      roles: [this.editedItem?.roles || null, Validators.required],
      enabled: [this.editedItem?.enabled || true],
      default: [this.editedItem?.default || false],
      deploymentId: [this.editedItem?.deploymentId || null, Validators.required],
      prompt: [this.editedItem?.prompt || '', Validators.required],
      lastMessages: [this.editedItem?.lastMessages || null, Validators.required],
      documentCount: [this.editedItem?.documentCount || null, Validators.required],

      rewritePrompt: [this.editedItem?.rewritePrompt || '', Validators.required],
      rewriteCount: [this.editedItem?.rewriteCount || null, Validators.required],
      rewriteDeploymentId: [this.editedItem?.rewriteDeploymentId || null, Validators.required],
      maxRewriteOutputTokens: [this.editedItem?.maxRewriteOutputTokens || null, Validators.required],

      retrievalCount: [this.editedItem?.retrievalCount || null, Validators.required],
      scoringProfile: [this.editedItem?.scoringProfile || '', Validators.required],
      embeddingDeploymentId: [this.editedItem?.embeddingDeploymentId || null, Validators.required],
      fieldMappingVectorFieldNames: [this.editedItem?.fieldMappingVectorFieldNames || [], Validators.required],
      rrfConstant: [this.editedItem?.rrfConstant || null, Validators.required],
      synthesisPrompt: [this.editedItem?.synthesisPrompt || '', Validators.required],
      synthesisReasoningEffortLevel: [this.editedItem?.synthesisReasoningEffortLevel || null, Validators.required],

      storageEndpoint: [this.editedItem?.storageEndpoint || '', Validators.required],
      storageContainer: [this.editedItem?.storageContainer || '', Validators.required],
    })

    if (this.editedItem?.id) {
      this.newIndexForm.addControl('id', this.fb.control(this.editedItem.id));
    }
  }

  onDialogCancel() {
    this.newIndexForm.reset();
    this.indexCancelled.emit();
  }

  onSubmit() {
    if (!FormUtils.checkIfFormGroupValid(this.newIndexForm, this.messageService)) {
      return;
    }

    if (!this.editedItem?.id) {
      this.createIndex(this.newIndexForm.value);
    } else  {
      this.updateIndex(this.newIndexForm.value);
    }
  }

  createIndex(indexValue: IndexAdminModel) {
    this.indexAdded.emit(indexValue);
  }

  updateIndex(indexValue: IndexAdminModel) {
    this.indexEdited.emit(indexValue);
  }


  getCompletionModels() {
    return this.models.filter(model => model.enabled && !model.embeddingModel);
  }

  getEmbeddingModels() {
    return this.models.filter(model => model.enabled && model.embeddingModel);
  }
}
